import React, { useState, useEffect, useRef } from "react";
import { MdDelete, MdEdit, MdAdd, MdFileDownload, MdSort } from "react-icons/md";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import Select from "react-select";
import { formatDate, formatTime, today, getPreviousDate } from "../../../Helper/Helper";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../../http";
import Spinner from "../../Spinner";
import * as echarts from "echarts";
import jsPDF from "jspdf";
import "jspdf-autotable";

// Custom CSS for styling
const customStyles = `
  .btn-group .btn.active-tab {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  .sort-dropdown {
    min-width: 200px;
  }
  .action-buttons .btn {
    padding: 0.25rem 0.5rem;
  }
  .action-buttons {
    display: flex;
    justify-content: center;
  }
  .blood-pressure-container table {
    font-size: 14px;
  }
  .blood-pressure-container table th,
  .blood-pressure-container table td {
    white-space: nowrap;
    padding: 0.5rem;
  }
`;

// Blood Pressure Chart Component
const BloodPressureChart = ({ data, loading }) => {
  const chartRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Helper to format x-axis date labels as "12 Mar 2025" and wrap text
  const formatAxisDateLabel = (value) => {
    if (!value) return value;

    try {
      let year, month, day;

      // Expecting YYYY-MM-DD or a parsable date string
      if (typeof value === "string" && value.includes("-")) {
        const parts = value.split("-");
        if (parts.length >= 3) {
          year = parts[0];
          month = parts[1];
          day = parts[2].substring(0, 2); // strip any time part if present
        }
      }

      // Fallback: parse with Date
      if (!year || !month || !day) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          year = d.getFullYear().toString();
          month = (d.getMonth() + 1).toString().padStart(2, "0");
          day = d.getDate().toString().padStart(2, "0");
        }
      }

      if (!year || !month || !day) return value;

      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      const monthIndex = parseInt(month, 10) - 1;
      const monthName = monthNames[monthIndex] || month;

      // Wrap into two lines: "12 Mar\n2025"
      return `${parseInt(day, 10)} ${monthName}\n${year}`;
    } catch (e) {
      return value;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!data || !data.systolic || !data.systolic.label || data.systolic.label.length === 0) {
      return;
    }

    // Prepare data points with colors for markers
    const systolicSeriesData = data.systolic.value.map((value, index) => ({
      value,
      itemStyle: {
        color: data.systolic.color?.[index] || "#4F81BC"
      }
    }));

    const diastolicSeriesData = data.diastolic.value.map((value, index) => ({
      value,
      itemStyle: {
        color: data.diastolic.color?.[index] || "#C0504D"
      }
    }));

    const option = {
      title: {
        text: 'Blood Pressure Trends',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        // Keep tooltip within chart area to avoid going off-screen
        confine: true,
        formatter: function (params) {
          const systolic = params.find(item => item.seriesName === 'Systolic');
          const diastolic = params.find(item => item.seriesName === 'Diastolic');
          const index = systolic.dataIndex;

          let status = "";
          if (data.systolic.status && data.systolic.status[index]) {
            status = ` (${data.systolic.status[index]})`;
          }

          return `
            ${systolic?.axisValue}<br/>
            ${systolic?.marker} ${systolic?.seriesName}: ${systolic?.data.value || systolic?.data} mmHg${status}<br/>
            ${diastolic?.marker} ${diastolic?.seriesName}: ${diastolic?.data.value || diastolic?.data} mmHg
          `;
        }
      },
      grid: {
        left: isMobile ? "3%" : "3%",
        right: isMobile ? "12%" : "6%",
        bottom: isMobile ? "2%" : "15%",
        containLabel: true
      },
      xAxis: {
        show: !isMobile,
        type: "category",
        data: data.systolic.label,
        axisLine: { show: true },
        axisTick: { show: true },
        axisLabel: {
          // Dynamically skip some labels when there are too many to avoid overlap
          interval: (index) => {
            const len = (data.systolic.label || []).length || 0;
            if (len > 20) return index % 4 === 0; // show every 4th label
            if (len > 12) return index % 3 === 0; // show every 3rd label
            if (len > 8) return index % 2 === 0;  // show every 2nd label
            return true; // show all when few
          },
          // Use custom formatter to change date format and wrap text
          formatter: (value) => formatAxisDateLabel(value),
          // Let ECharts hide overlapping labels as a fallback
          hideOverlap: true,
          margin: 12,
          rotate: 0,
          fontSize: 10,
        }
      },
      yAxis: {
        type: "value",
        name: "mmHg",
        nameLocation: "end",
        nameTextStyle: {
          padding: [0, 0, 0, 16]
        },
        axisLabel: {
          fontSize: 12,
          align: "end",
          margin: 30,
          hideOverlap: true,
        },
        min: Math.min(
          ...data.systolic.value.filter(val => val !== null),
          ...data.diastolic.value.filter(val => val !== null)
        ) - 10,
        max: Math.max(
          ...data.systolic.value.filter(val => val !== null),
          ...data.diastolic.value.filter(val => val !== null)
        ) + 10,
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      visualMap: [
        {
          show: false,
          dimension: 0,
          seriesIndex: 0,
          pieces: data.systolic.color?.map((clr, idx) => ({
            gt: idx - 1,
            lte: idx,
            color: clr
          }))
        },
        {
          show: false,
          dimension: 0,
          seriesIndex: 1,
          pieces: data.diastolic.color?.map((clr, idx) => ({
            gt: idx - 1,
            lte: idx,
            color: clr
          }))
        }
      ],
      legend: {
        data: ["Systolic", "Diastolic"],
        bottom: 10,
      },
      series: [
        {
          name: "Systolic",
          data: systolicSeriesData,
          type: "line",
          smooth: true,
          symbol: "circle",
          // Match other charts: smaller symbols and down-sampling
          symbolSize: 5,
          showAllSymbol: "auto",
          sampling: "lttb",
          lineStyle: {
            width: 3
          },
          markPoint: {
            data: [
              { type: 'max', name: 'Max' },
              { type: 'min', name: 'Min' }
            ]
          },
          markLine: {   // 🔹 Add average line
            data: [
              { type: 'average', name: 'Avg' }
            ],
            lineStyle: {
              type: "dashed",
              // color: "#4F81BC"
            },
            label: {
              show: true,
              formatter: ({ value }) => value.toFixed(1)
            }
          }
        },
        {
          name: "Diastolic",
          data: diastolicSeriesData,
          type: "line",
          smooth: true,
          symbol: "circle",
          // Match other charts: smaller symbols and down-sampling
          symbolSize: 5,
          showAllSymbol: "auto",
          sampling: "lttb",
          lineStyle: {
            width: 3
          },
          markPoint: {
            data: [
              { type: 'max', name: 'Max' },
              { type: 'min', name: 'Min' }
            ]
          },
          markLine: {   // 🔹 Add average line
            data: [
              { type: 'average', name: 'Avg' }
            ],
            lineStyle: {
              type: "dashed",
              // color: "#C0504D"
            },
            label: {
              show: true,
              formatter: ({ value }) => value.toFixed(1)
            }
          }
        }
      ],
    };

    const chart = echarts.init(chartRef.current);
    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [data]);

  return loading ? (
    <div className="text-center">
      <Spinner />
    </div>
  ) : (
    <>
      <div className="pt-4 text-center fw-bold">
        {!data || !data.systolic || data.systolic.label.length === 0 ? "No Record Available" : ""}
      </div>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height:
            data?.systolic?.label?.length > 0 ? "400px" : "0px",
        }}
      />

      {/* <div ref={chartRef} style={{
        width: "100%",
        height: data && data.label && data.label.length > 0 ? "400px" : "0px"
      }} /> */}
    </>
  );
};

// Main Blood Pressure Component
function BloodPressure() {
  const patientData = useSelector((store) => store.patient.patientLogin);
  const [viewMode, setViewMode] = useState("chart");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [graphData, setGraphData] = useState({});
  const [editData, setEditData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [addData, setAddData] = useState({
    systolic: "",
    diastolic: "",
    pulse: "",
    record_date: "",
    record_time: "",
    // record_date: today,
    // record_time: new Date().toTimeString().slice(0, 5),
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedTimeStamp, setSelectedTimeStamp] = useState({ value: "4-week", label: "4 Weeks" });
  const [sortOption, setSortOption] = useState({ value: 'record_date-desc', label: 'Date (Newest First)' });

  const timeStamp = [
    { value: "1-week", label: "1 Week" },
    { value: "2-week", label: "2 Weeks" },
    { value: "3-week", label: "3 Weeks" },
    { value: "4-week", label: "4 Weeks" },
  ];

  const sortOptions = [
    { value: 'record_date-desc', label: 'Date (Newest First)' },
    { value: 'record_date-asc', label: 'Date (Oldest First)' },
    { value: 'systolic-desc', label: 'Systolic (High to Low)' },
    { value: 'systolic-asc', label: 'Systolic (Low to High)' },
    { value: 'diastolic-desc', label: 'Diastolic (High to Low)' },
    { value: 'diastolic-asc', label: 'Diastolic (Low to High)' },
  ];

  useEffect(() => {
    // Add custom styles
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);

    window.scrollTo(0, 0);
    fetchData();
    fetchGraphData();

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchGraphData();
  }, [selectedTimeStamp]);

  useEffect(() => {
    if (sortOption) {
      const [key, direction] = sortOption.value.split('-');
      handleSort(key, direction);
    }
  }, [sortOption]);

  const fetchData = async (filter = {}) => {
    setIsLoading(true);
    try {
      const response = await SendPostRequest("healthcare/blood_pressure/grid", {
        logged_id: patientData?.logged_id,
        filter: filter
      });

      if (response?.status === "success") {
        const data = Array.isArray(response?.data) ? response.data : [];
        setGetData(data);
      } else {
        toast.error(response?.message);
        setGetData([]);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
      setGetData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGraphData = async () => {
    setLoadingGraph(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_pressure/graph",
        {
          logged_id: patientData?.logged_id,
          timespan: selectedTimeStamp?.value || "4-week",
        }
      );
      if (response?.status === "success") {
        setGraphData(response?.data || {});
      } else {
        toast.error(response?.message);
        setGraphData({});
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch graph data");
      setGraphData({});
    } finally {
      setLoadingGraph(false);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });

    const sortedData = [...getData].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setGetData(sortedData);
  };

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setValidationErrors({});
  };
  const handleKeyDown = (e) => {
    // Prevent minus sign and decimal point input
    if (e.key === '-' || e.key === '.') {
      e.preventDefault();
    }
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Prevent minus signs and decimal points
    if (name === 'systolic' || name === 'diastolic' || name === 'pulse') {
      // Remove any minus signs or decimal points
      const sanitizedValue = value.replace(/[-.]/g, '');

      // Update the input value directly
      e.target.value = sanitizedValue;
    }

    // Validate inputs
    let errors = { ...validationErrors };

    // Check for non-integer values
    if ((name === 'systolic' || name === 'diastolic' || name === 'pulse') && !/^\d*$/.test(value)) {
      errors[name] = 'Please enter a valid whole number';
      setValidationErrors(errors);
      return;
    }

    // Validate ranges
    if (name === 'systolic') {
      const systolicValue = parseInt(value) || 0;
      if (systolicValue < 40 || systolicValue > 370) {
        errors.systolic = 'Systolic must be between 40-370 mmHg';
      } else {
        delete errors.systolic;

        // Check if diastolic is already set and validate
        const diastolicValue = editData.id ? parseInt(editData.diastolic) || 0 : parseInt(addData.diastolic) || 0;
        if (diastolicValue > 0 && systolicValue <= diastolicValue) {
          errors.systolic = 'Systolic value must be greater than diastolic value';
        } else if (errors.diastolic && errors.diastolic.includes('Diastolic value must be smaller than systolic value')) {
          delete errors.diastolic;
        }
      }
    }

    if (name === 'diastolic') {
      const diastolicValue = parseInt(value) || 0;
      if (diastolicValue < 20 || diastolicValue > 260) {
        errors.diastolic = 'Diastolic must be between 20-260 mmHg';
      } else {
        delete errors.diastolic;

        // Check if systolic is already set and validate
        const systolicValue = editData.id ? parseInt(editData.systolic) || 0 : parseInt(addData.systolic) || 0;
        if (systolicValue > 0 && diastolicValue >= systolicValue) {
          errors.diastolic = 'Diastolic value must be smaller than systolic value';
        } else if (errors.systolic && errors.systolic.includes('Systolic value cannot be smaller')) {
          delete errors.systolic;
        }
      }
    }

    if (name === 'pulse') {
      const pulseValue = parseInt(value) || 0;
      if (pulseValue < 7 || pulseValue > 480) {
        errors.pulse = 'Pulse must be between 7-480 bpm';
      } else {
        delete errors.pulse;
      }
    }

    setValidationErrors(errors);

    if (editData.id) {
      setEditData((prv) => ({ ...prv, [name]: value }));
    } else {
      setAddData((prv) => ({ ...prv, [name]: value }));
    }
  };

  const validateForm = (formData) => {
    const errors = {};

    const systolicValue = parseInt(formData.systolic) || 0;
    const diastolicValue = parseInt(formData.diastolic) || 0;
    const pulseValue = parseInt(formData.pulse) || 0;

    if (!formData.systolic || systolicValue < 40 || systolicValue > 370) {
      errors.systolic = 'Systolic must be between 40-370 mmHg';
    }

    if (!formData.diastolic || diastolicValue < 20 || diastolicValue > 260) {
      errors.diastolic = 'Diastolic must be between 20-260 mmHg';
    }

    if (systolicValue > 0 && diastolicValue > 0 && systolicValue <= diastolicValue) {
      errors.systolic = 'Systolic value must be greater than diastolic value';
    }

    if (!formData.pulse || pulseValue < 7 || pulseValue > 480) {
      errors.pulse = 'Pulse must be between 7-480 bpm';
    }

    if (!formData.record_date) {
      errors.record_date = 'Date is required';
    }

    if (!formData.record_time) {
      errors.record_time = 'Time is required';
    }
    if (formData.record_date && formData.record_time) {
      const selectedDateTime = new Date(`${formData.record_date}T${formData.record_time}`);
      const now = new Date();
      if (selectedDateTime > now) {
        errors.record_time = 'Future times are not allowed';
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm(editData)) {
      return;
    }

    const data = {
      logged_id: patientData?.logged_id,
      record_id: editData?.id,
      form_data: {
        systolic: editData?.systolic,
        diastolic: editData?.diastolic,
        pulse: editData?.pulse,
        record_date: editData?.record_date,
        record_time: editData?.record_time,
        notes: editData?.notes,
      },
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_pressure/edit",
        data
      );
      if (response?.status === "success") {
        toast.success("Blood Pressure record updated Successfully");
        handleModalClose();
        fetchData(filterData);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to update record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDelete = async () => {
    const data = {
      logged_id: patientData?.logged_id,
      record_id: deleteId,
    };

    setLoadingDelete(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_pressure/delete",
        data
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        fetchData(filterData);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(addData)) {
      return;
    }

    const data = {
      logged_id: patientData?.logged_id,
      form_data: addData,
    };

    setIsLoading(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_pressure/add",
        data
      );
      if (response?.status === "success") {
        toast.success("Record has been added successfully");
        handleModalClose();
        setAddData({
          systolic: "",
          diastolic: "",
          pulse: "",
          record_date: "",
          record_time: "",
          notes: "",
        });
        fetchData(filterData);
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to add record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = async (e) => {
    e.preventDefault();

    if (filterData.date_from && filterData.date_to) {
      if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
        toast.error("From date cannot be after to date");
        return;
      }
    }

    fetchData(filterData);
  };

  const clearFilter = () => {
    setFilterData({});
    fetchData({});
  };

  const formatMonthShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const exportToCSV = () => {
    if (getData.length === 0) {
      toast.info("No data available to export");
      return;
    }

    const headers = [
      "SR.No",
      "Record Date",
      "Record Time",
      "Systolic (mmHg)",
      "Diastolic (mmHg)",
      "Pulse (bpm)",
      "Note",
      "Status"
    ];

    const csvData = getData.map((item, index) => [
      index + 1,
      formatDate(item.record_date),
      formatTime(item.record_time),
      item.systolic,
      item.diastolic,
      item.pulse,
      item.notes || "--",
      item.status || "--"
    ]);

    // Summary values
    const systolicValues = getData.map(item => parseInt(item.systolic));
    const diastolicValues = getData.map(item => parseInt(item.diastolic));
    const pulseValues = getData.map(item => parseInt(item.pulse));

    const systolicAvg = systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length;
    const diastolicAvg = diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length;
    const pulseAvg = pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length;

    const systolicMax = Math.max(...systolicValues);
    const diastolicMax = Math.max(...diastolicValues);
    const pulseMax = Math.max(...pulseValues);

    const systolicMin = Math.min(...systolicValues);
    const diastolicMin = Math.min(...diastolicValues);
    const pulseMin = Math.min(...pulseValues);

    // Final CSV array
    let csvArray = [];
    csvArray.push(headers);
    csvArray.push(...csvData);

    // Empty row before summary
    csvArray.push([]);

    // Summary rows
    csvArray.push(["Summary Statistics"]);
    csvArray.push(["Systolic - Average", systolicAvg.toFixed(1)]);
    csvArray.push(["Systolic - Maximum", systolicMax]);
    csvArray.push(["Systolic - Minimum", systolicMin]);
    csvArray.push(["Diastolic - Average", diastolicAvg.toFixed(1)]);
    csvArray.push(["Diastolic - Maximum", diastolicMax]);
    csvArray.push(["Diastolic - Minimum", diastolicMin]);
    csvArray.push(["Pulse - Average", pulseAvg.toFixed(1)]);
    csvArray.push(["Pulse - Maximum", pulseMax]);
    csvArray.push(["Pulse - Minimum", pulseMin]);

    // Add report generated on
    csvArray.push([]);
    csvArray.push(["Report Generated On", formatDate(new Date())]);

    // Convert to CSV string
    const csvContent = csvArray
      .map((row) => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `blood_pressure_${today}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const exportToPDF = () => {
    if (getData.length === 0) {
      toast.info("No data available to export");
      return;
    }

    import("jspdf").then((jsPDFModule) => {
      import("jspdf-autotable").then((autoTableModule) => {
        const { jsPDF } = jsPDFModule;
        const { default: autoTable } = autoTableModule;

        const doc = new jsPDF();

        // Title
        doc.setFontSize(18);
        doc.text("Blood Pressure Records", 14, 22);

        // Report generated date
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

        // Date range if filtered
        if (filterData?.date_from && filterData?.date_to) {
          doc.text(
            `Date Range: ${formatDate(filterData.date_from)} to ${formatDate(filterData.date_to)}`,
            14,
            40
          );
        }

        // Table body with SR.No
        const tableBody = getData.map((item, index) => [
          index + 1,
          formatDate(item.record_date),
          formatTime(item.record_time),
          item.systolic,
          item.diastolic,
          item.pulse,
          item.notes || "--",
          item.status || "--",
        ]);

        // Table
        autoTable(doc, {
          startY: filterData?.date_from && filterData?.date_to ? 50 : 40,
          head: [
            [
              "SR.No",
              "Date",
              "Time",
              "Systolic (mmHg)",
              "Diastolic (mmHg)",
              "Pulse (bpm)",
              "Notes",
              "Status",
            ],
          ],
          body: tableBody,
          theme: "grid",
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: [255, 255, 255],
            fontSize: 10,
          },
          bodyStyles: {
            fontSize: 9,
          },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 25 },
            2: { cellWidth: 15 },
            3: { cellWidth: 25 },
            4: { cellWidth: 25 },
            5: { cellWidth: 15 },
            6: { cellWidth: 30 },
            7: { cellWidth: 35 },
          },
        });

        // Summary stats
        const systolicValues = getData.map((item) => parseInt(item.systolic));
        const diastolicValues = getData.map((item) => parseInt(item.diastolic));
        const pulseValues = getData.map((item) => parseInt(item.pulse));

        const avg = (arr) =>
          arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1) : 0;
        const max = (arr) => (arr.length ? Math.max(...arr) : 0);
        const min = (arr) => (arr.length ? Math.min(...arr) : 0);

        let finalY = doc.lastAutoTable.finalY + 15;
        if (finalY > 270) {
          doc.addPage();
          finalY = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Summary Statistics", 14, finalY);

        doc.setFontSize(10);
        doc.text(
          `Systolic - Avg: ${avg(systolicValues)}, Max: ${max(systolicValues)}, Min: ${min(systolicValues)}`,
          14,
          finalY + 10
        );
        doc.text(
          `Diastolic - Avg: ${avg(diastolicValues)}, Max: ${max(diastolicValues)}, Min: ${min(diastolicValues)}`,
          14,
          finalY + 17
        );
        doc.text(
          `Pulse - Avg: ${avg(pulseValues)}, Max: ${max(pulseValues)}, Min: ${min(pulseValues)}`,
          14,
          finalY + 24
        );

        // Save file
        doc.save(`blood_pressure_records_${today}.pdf`);
      });
    }).catch((error) => {
      console.error("Error loading PDF libraries:", error);
      toast.error("Failed to generate PDF");
    });
  };


  // const exportToPDF = () => {
  //   if (getData.length === 0) {
  //     toast.info("No data available to export");
  //     return;
  //   }

  //   import('jspdf').then(jsPDFModule => {
  //     import('jspdf-autotable').then(autoTableModule => {
  //       const { jsPDF } = jsPDFModule;
  //       const { default: autoTable } = autoTableModule;

  //       const doc = new jsPDF();

  //       // Title
  //       doc.setFontSize(18);
  //       doc.text("Blood Pressure Records", 14, 22);
  //       doc.setFontSize(11);
  //       doc.setTextColor(100);
  //       doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

  //       // Date range if filtered
  //       if (filterData?.date_from && filterData?.date_to) {
  //         doc.text(`Date Range: ${formatDate(filterData.date_from)} to ${formatDate(filterData.date_to)}`, 14, 40);
  //       }

  //       // Prepare table body with SR.No
  //       const tableBody = getData.map((item, index) => [
  //         index + 1,
  //         formatDate(item.record_date),
  //         formatTime(item.record_time),
  //         item.systolic,
  //         item.diastolic,
  //         item.pulse,
  //         item.notes || "-",
  //         item.status || "-"
  //       ]);

  //       // Generate table
  //       autoTable(doc, {
  //         startY: filterData?.date_from && filterData?.date_to ? 50 : 40,
  //         head: [["SR.No", "Date", "Time", "Systolic (mmHg)", "Diastolic (mmHg)", "Pulse (bpm)", "Notes", "Status"]],
  //         body: tableBody,
  //         theme: 'grid',
  //         headStyles: {
  //           fillColor: [66, 139, 202],
  //           textColor: [255, 255, 255],
  //           fontSize: 10,
  //         },
  //         bodyStyles: {
  //           fontSize: 9,
  //         },
  //         columnStyles: {
  //           0: { cellWidth: 15 },
  //           1: { cellWidth: 25 },
  //           2: { cellWidth: 20 },
  //           3: { cellWidth: 25 },
  //           4: { cellWidth: 25 },
  //           5: { cellWidth: 20 },
  //           6: { cellWidth: 40 },
  //           7: { cellWidth: 20 }
  //         }
  //       });

  //       // Summary statistics
  //       const systolicValues = getData.map(item => parseInt(item.systolic));
  //       const diastolicValues = getData.map(item => parseInt(item.diastolic));
  //       const pulseValues = getData.map(item => parseInt(item.pulse));

  //       const systolicAvg = systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length;
  //       const diastolicAvg = diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length;
  //       const pulseAvg = pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length;

  //       const systolicMax = Math.max(...systolicValues);
  //       const diastolicMax = Math.max(...diastolicValues);
  //       const pulseMax = Math.max(...pulseValues);

  //       const systolicMin = Math.min(...systolicValues);
  //       const diastolicMin = Math.min(...diastolicValues);
  //       const pulseMin = Math.min(...pulseValues);

  //       let finalY = doc.lastAutoTable.finalY + 15;

  //       doc.setFontSize(14);
  //       doc.setTextColor(0, 0, 0);
  //       doc.text("Summary Statistics", 14, finalY);

  //       doc.setFontSize(10);
  //       doc.text(`Systolic - Avg: ${systolicAvg.toFixed(1)}, Max: ${systolicMax}, Min: ${systolicMin}`, 14, finalY + 10);
  //       doc.text(`Diastolic - Avg: ${diastolicAvg.toFixed(1)}, Max: ${diastolicMax}, Min: ${diastolicMin}`, 14, finalY + 17);
  //       doc.text(`Pulse - Avg: ${pulseAvg.toFixed(1)}, Max: ${pulseMax}, Min: ${pulseMin}`, 14, finalY + 24);

  //       // Save file
  //       doc.save(`blood_pressure_records_${today}.pdf`);
  //     });
  //   }).catch(error => {
  //     console.error("Error loading PDF libraries:", error);
  //     toast.error("Failed to generate PDF");
  //   });
  // };

  const getStatusClass = (status) => {
    if (!status) return "secondary";

    if (status.includes("Crisis") || status.includes("Hypertensive")) return "danger";
    if (status.includes("High") || status.includes("Stage 2")) return "danger";
    if (status.includes("Stage 1") || status.includes("Elevated")) return "warning";
    if (status.includes("Normal")) return "success";
    return "secondary";
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="blood-pressure-container Prescripition">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h3>Blood Pressure</h3>
        <div
          className="d-flex gap-2"
          style={{
            justifyContent: isMobile ? "space-between" : "",
            width: isMobile ? "100%" : "",
          }}
        >
          <button
            className="btn btn-default d-flex align-items-center"
            onClick={handleModalOpen}
            style={{ alignItems: "center" }}
          >
            <MdAdd className="me-1" /> Add Record
          </button>
          <div>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToCSV}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> CSV
            </button>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToPDF}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`chart-views-btn ${
                viewMode === "chart"
                  ? "chart-active-views-btn"
                  : "chart-views-btn"
              }`}
              onClick={() => setViewMode("chart")}
            >
              Chart View
            </button>
            <button
              type="button"
              className={`chart-views-btn-second ${
                viewMode === "table"
                  ? "chart-active-views-btn"
                  : "chart-views-btn-second"
              }`}
              onClick={() => setViewMode("table")}
            >
              Table View
            </button>
          </div>

          {viewMode === "chart" && (
            <div className="d-flex align-items-center">
              <span className="me-2">Time Range:</span>
              <Select
                className="time-range-select"
                value={selectedTimeStamp}
                options={timeStamp}
                onChange={setSelectedTimeStamp}
                isSearchable={false}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minWidth: "120px",
                  }),
                }}
              />
            </div>
          )}
        </div>

        {viewMode === "chart" ? (
          <BloodPressureChart data={graphData} loading={loadingGraph} />
        ) : (
          <>
            {/* <div className="d-flex justify-content-between align-items-center mb-3"> */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <form
                onSubmit={handleFilter}
                className="d-flex gap-2 align-items-center justify-content-between flex-wrap"
              >
                <div>
                  <label htmlFor="from" className="form-label mb-0">
                    From:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_from"
                    className="form-control"
                    value={filterData?.date_from || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>
                <div>
                  <label htmlFor="to" className="form-label mb-0">
                    To:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_to"
                    className="form-control"
                    value={filterData?.date_to || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-default"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={clearFilter}
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Clear
                </button>

                <div
                  className="d-flex align-items-center ms-2"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  <MdSort className="me-1" />
                  <Select
                    className="sort-select"
                    value={sortOption}
                    options={sortOptions}
                    onChange={setSortOption}
                    isSearchable={false}
                    placeholder="Sort by..."
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minWidth: "180px",
                      }),
                    }}
                  />
                </div>
              </form>
            </div>
            {/* </div> */}

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table mt-2">
                  <thead className="thead-light">
                    <tr>
                      <th
                        scope="col"
                        style={{ width: "10%", textAlign: "center" }}
                      >
                        SR.No
                      </th>
                      <th scope="col">Record Date</th>
                      <th scope="col">Record Time</th>
                      <th scope="col">Systolic(mmHg)</th>
                      <th scope="col">Diastolic(mmHg)</th>
                      <th scope="col">Pulse(bpm)</th>
                      {/* <th scope="col">Note</th> */}
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getData.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          No blood pressure records found
                        </td>
                      </tr>
                    ) : (
                      getData.map((item, index) => {
                        const statusColor = item?.color || "#000";

                        return (
                          <tr key={item.id}>
                            <td style={{ textAlign: "center" }}>{index + 1}</td>
                            <td>{formatMonthShort(item.record_date)}</td>
                            <td>{formatTime(item.record_time)}</td>
                            <td>{item.systolic}</td>
                            <td>{item.diastolic}</td>
                            <td>{item.pulse}</td>
                            {/* <td>{item.notes || "--"}</td> */}
                            <td>
                              <span
                                className={`badge`}
                                style={{
                                  backgroundColor: statusColor,
                                  color: "#fff",
                                  textTransform: "capitalize",
                                  fontSize: "10px",
                                }}
                              >
                                {item.status || "Unknown"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-default me-1"
                                  onClick={() => {
                                    setEditData(item);
                                    handleModalOpen();
                                  }}
                                  title="Edit"
                                >
                                  <MdEdit />
                                </button>
                                <button
                                  className="btn btn-default"
                                  onClick={() => {
                                    setDeleteId(item.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                  disabled={LoadingDelete}
                                  title="Delete"
                                >
                                  {LoadingDelete && deleteId === item.id ? (
                                    <SpinnerBoot size="sm" />
                                  ) : (
                                    <MdDelete />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleModalClose}
        size="lg"
        className="blood-pressure-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editData.id
              ? "Edit Blood Pressure Record"
              : "Add Blood Pressure Record"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={editData.id ? handleEdit : handleAdd}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Diastolic (mmHg)*</label>
                  <input
                    type="number"
                    className={`form-control mb-0 ${
                      validationErrors.diastolic ? "is-invalid" : ""
                    }`}
                    placeholder="20-260"
                    value={editData.id ? editData.diastolic : addData.diastolic}
                    name="diastolic"
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    min="20"
                    max="260"
                    required
                  />
                  {validationErrors.diastolic && (
                    <div
                      className="invalid-feedback"
                      style={{ fontSize: "11px" }}
                    >
                      {validationErrors.diastolic}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Systolic (mmHg)*</label>
                  <input
                    type="number"
                    className={`form-control mb-0 ${
                      validationErrors.systolic ? "is-invalid" : ""
                    }`}
                    placeholder="40-370"
                    value={editData.id ? editData.systolic : addData.systolic}
                    name="systolic"
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    min="40"
                    max="370"
                    required
                  />
                  {validationErrors.systolic && (
                    <div
                      className="invalid-feedback "
                      style={{ fontSize: "11px" }}
                    >
                      {validationErrors.systolic}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Pulse (bpm)*</label>
                  <input
                    type="number"
                    className={`form-control mb-0 ${
                      validationErrors.pulse ? "is-invalid" : ""
                    }`}
                    placeholder="7-480"
                    value={editData.id ? editData.pulse : addData.pulse}
                    name="pulse"
                    onChange={handleOnChange}
                    onKeyDown={handleKeyDown}
                    min="7"
                    max="480"
                    required
                  />
                  {validationErrors.pulse && (
                    <div
                      className="invalid-feedback"
                      style={{ fontSize: "11px" }}
                    >
                      {validationErrors.pulse}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Date*</label>
                  <input
                    style={{ height: "50px" }}
                    type="date"
                    className={`form-control ${
                      validationErrors.record_date ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_date : addData.record_date
                    }
                    name="record_date"
                    onChange={handleOnChange}
                    max={today}
                    required
                  />
                  {validationErrors.record_date && (
                    <div className="invalid-feedback">
                      {validationErrors.record_date}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Time*</label>
                  <input
                    style={{ height: "50px" }}
                    type="time"
                    className={`form-control mb-0 ${
                      validationErrors.record_time ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_time : addData.record_time
                    }
                    name="record_time"
                    onChange={handleOnChange}
                    required
                  />
                  {validationErrors.record_time && (
                    <div className="invalid-feedback mt-0">
                      {validationErrors.record_time}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Add any notes about this measurement"
                    value={editData.id ? editData.notes : addData.notes}
                    name="notes"
                    onChange={handleOnChange}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn  btn-default"
              variant="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn  btn-default"
              variant="btn btn-default"
              type="submit"
              disabled={LoadingEdit || isLoading}
            >
              {LoadingEdit || isLoading ? "Saving..." : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this blood pressure record? This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BloodPressure;